import React, { useEffect } from 'react';
import { Target, Minimize2, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AnalysisProgressCircle } from './AnalysisProgressCircle';
import { AnalysisStageList } from './AnalysisStageList';
import { AnalysisMinimizedIndicator } from './AnalysisMinimizedIndicator';
import { useAnalysisProgress } from '../hooks/useAnalysisProgress';
import type { AnalysisStage } from '@/types/analysisProgress';
import { cn } from '@/lib/utils';

interface AnalysisPizzaTrackerProps {
  swingRunId: string;
  minimized?: boolean;
  onMinimizeToggle?: () => void;
  onComplete?: () => void;
  className?: string;
  uploadProgress?: number | null; // 0-100, for upload stage
  isUploading?: boolean; // true when actively uploading
}

const ANALYSIS_TIPS = [
  "Focus on one swing fault at a time for faster progress.",
  "Practice drills 3x per week for best results.",
  "Your swing quality will improve with each upload.",
  "Consistency beats perfection. Small improvements add up!",
  "Record your swings from down-the-line and face-on angles.",
  "Most golfers see improvement within 2-3 weeks of focused practice.",
  "Your training plan adapts as your swing improves.",
  "Warming up before practice prevents injury and improves learning.",
];

export function AnalysisPizzaTracker({
  swingRunId,
  minimized = false,
  onMinimizeToggle,
  onComplete,
  className,
  uploadProgress,
  isUploading = false,
}: AnalysisPizzaTrackerProps) {
  const { stage, progress, statusText, isLoading, error, estimatedTimeRemaining } =
    useAnalysisProgress(swingRunId === 'uploading' ? '' : swingRunId);

  // If we're uploading, use upload progress mapped to 0-20% range
  // Otherwise use the polled progress, but ensure it doesn't go backwards
  const displayProgress = isUploading && uploadProgress !== null
    ? Math.round((uploadProgress / 100) * 20) // Map 0-100% upload to 0-20% tracker
    : Math.max(20, progress); // Ensure progress never goes below 20% after upload completes
  
  const displayStage: AnalysisStage = isUploading ? 'uploading' : stage;
  const displayStatusText = isUploading 
    ? `Uploading video... ${uploadProgress !== null ? Math.round(uploadProgress) : 0}%`
    : statusText;

  // Debug logging
  useEffect(() => {
    console.log('[AnalysisPizzaTracker] Display state:', {
      swingRunId,
      isUploading,
      uploadProgress,
      displayProgress,
      displayStage,
      displayStatusText,
      polledStage: stage,
      polledProgress: progress,
      error: error?.message,
    });
  }, [swingRunId, isUploading, uploadProgress, displayProgress, displayStage, displayStatusText, stage, progress, error]);

  // Rotate tip every 10 seconds
  const [currentTipIndex, setCurrentTipIndex] = React.useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % ANALYSIS_TIPS.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Call onComplete when stage is complete
  useEffect(() => {
    if (stage === 'complete' && onComplete) {
      // Small delay to show completion state
      const timer = setTimeout(() => {
        onComplete();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [stage, onComplete]);

  // Always show tracker - don't block on errors
  // The endpoint might not be deployed yet, but we should still show progress UI
  // Errors are logged but don't block the UI

  // Minimized view
  if (minimized) {
    return (
      <div className={cn("fixed bottom-20 right-4", className)}>
        <AnalysisMinimizedIndicator
          progress={displayProgress}
          onClick={onMinimizeToggle || (() => {})}
        />
      </div>
    );
  }

  // Full view
  const isComplete = displayStage === 'complete';

  return (
    <Card className={cn("max-w-2xl mx-auto", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#196644]" />
            {isComplete ? 'Analysis Complete!' : 'Analyzing Your Swing'}
          </CardTitle>
          {onMinimizeToggle && !isComplete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMinimizeToggle}
              className="text-gray-500 hover:text-gray-700"
            >
              <Minimize2 className="w-4 h-4 mr-1" />
              Minimize
            </Button>
          )}
        </div>
        {!isComplete && (
          <p className="text-sm text-muted-foreground mt-2">
            This usually takes 2-3 minutes. Feel free to explore the app!
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Circle */}
        <div className="flex justify-center">
          <AnalysisProgressCircle progress={displayProgress} />
        </div>

        {/* Status Text */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mt-2">{displayStatusText}</p>
          {estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Estimated {estimatedTimeRemaining}s remaining
            </p>
          )}
          {error && (
            <p className="text-xs text-amber-600 mt-1">
              Note: Progress updates may be delayed
            </p>
          )}
        </div>

        {/* Stage List */}
        <div className="border-t pt-6">
          <AnalysisStageList currentStage={displayStage} />
        </div>

        {/* Tip Section */}
        {!isComplete && (
          <div className="bg-gradient-to-r from-[#F9C94E]/10 to-[#A7F3A1]/10 rounded-xl p-4 border border-[#F9C94E]/20">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-[#F9C94E] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">{ANALYSIS_TIPS[currentTipIndex]}</p>
            </div>
          </div>
        )}

        {/* Complete State */}
        {isComplete && (
          <div className="text-center space-y-4">
            <div className="text-green-600 text-lg font-medium">
              Your swing analysis is ready!
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

